/* eslint-disable no-use-before-define */
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, matchPath, Link as RouterLink } from 'react-router-dom';
import PerfectScrollbar from 'react-perfect-scrollbar';
import PropTypes from 'prop-types';
import {
  Avatar,
  Box,
  Divider,
  Drawer,
  Hidden,
  List,
  ListSubheader,
  Typography,
  makeStyles
} from '@material-ui/core';
import LogoBlanco from '../../../assets/img/LogoBlanco.svg';
import userAvatar from '../../../assets/img/avatar-default.png';
import NavItem from './NavItem';
import { ADMIN, ADMIN_RECINTO } from '../../../helpers/Constants';
import CanchaSimpleIcon from '../../../components/Icons/CanchaSimpleIcon';
import EstadioIcon from '../../../components/Icons/EstadioIcon';
import ConfiguracionesIcon from '../../../components/Icons/ConfiguracionesIcon';
import ListadoIcon from '../../../components/Icons/ListadoIcon';
import InformacionContactoIcon from './../../../components/Icons/InformacionContactoIcon';

function renderNavItems({ items, pathname, depth = 0 }) {
  return (
    <List disablePadding>
      {items.reduce(
        (acc, item) => reduceChildRoutes({ acc, item, pathname, depth }),
        []
      )}
    </List>
  );
}

function reduceChildRoutes({ acc, pathname, item, depth }) {
  const key = item.title + depth;

  if (item.items) {
    const open = matchPath(pathname, {
      path: item.href,
      exact: false
    });

    acc.push(
      <NavItem
        depth={depth}
        icon={item.icon}
        info={item.info}
        key={key}
        open={Boolean(open)}
        title={item.title}
      >
        {renderNavItems({
          depth: depth + 1,
          pathname,
          items: item.items
        })}
      </NavItem>
    );
  } else {
    acc.push(
      <NavItem
        depth={depth}
        href={item.href}
        icon={item.icon}
        info={item.info}
        key={key}
        title={item.title}
      />
    );
  }

  return acc;
}

const useStyles = makeStyles(() => ({
  mobileDrawer: {
    width: 256
  },
  desktopDrawer: {
    width: 256,
    top: 64,
    height: 'calc(100% - 64px)',
    backgroundColor: 'transparent',
    borderRightWidth: 0,
  },
  avatar: {
    width: 64,
    height: 64
  },
}));

const NavBar = ({ onMobileClose, openMobile }) => {
  const classes = useStyles();
  const location = useLocation();
  const { user } = useSelector(state => state.auth);
  const idRecintoSelected = useSelector(state => state.users.idRecinto);

  useEffect(() => {
    if (openMobile && onMobileClose) {
      onMobileClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const routesGeneralAdmin = () => {
    const routes = [
      {
        title: 'Recintos',
        icon: EstadioIcon,
        href: '/admin/recintos'
      },
      {
        title: 'Usuarios',
        icon: InformacionContactoIcon,
        href: '/admin/usuarios'
      },
      {
        title: 'Mantenedores',
        icon: ConfiguracionesIcon,
        href: '/admin/mantenedores'
      }
    ];

    if (idRecintoSelected) {
      routes.push({
        title: 'Canchas',
        icon: CanchaSimpleIcon,
        href: `/admin-recinto/recintos/${idRecintoSelected}/canchas`
      });
      routes.push({
        title: 'Horarios',
        icon: ListadoIcon,
        href: `/admin-recinto/recintos/${idRecintoSelected}/horarios`
      });
    }
    return routes;
  };

  const sections = user => {
    const routes = [];
    switch (user.tipo) {
      case ADMIN:
        routes.push({
          subheader: 'Administrador General',
          items: routesGeneralAdmin()
        });
        break;

      case ADMIN_RECINTO:
        routes.push({
          subheader: 'Administrador de Recinto',
          items: [
            {
              title: 'Recinto',
              icon: EstadioIcon,
              href: `/admin-recinto/recintos/${user.idRecinto}`
            },
            {
              title: 'Canchas',
              icon: CanchaSimpleIcon,
              href: `/admin-recinto/recintos/${user.idRecinto}/canchas`
            },
            {
              title: 'Procedimientos',
              icon: ListadoIcon,
              href: `/admin-recinto/recintos/${user.idRecinto}/procedimientos`
            },
            {
              title: 'Horarios',
              icon: ListadoIcon,
              href: `/admin-recinto/recintos/${user.idRecinto}/horarios`
            }
          ]
        });
        break;
      default:
        break;
    }
    return routes;
  };

  const content = (
    <Box height="100%" display="flex" flexDirection="column">
      <PerfectScrollbar options={{ suppressScrollX: true }}>
        <Hidden lgUp>
          <Box p={2} mt={2} display="flex" justifyContent="center">
            <RouterLink to="/">
              {/* Aqui se puede cambiar el logo */}
              <img src={LogoBlanco} alt="logo" style={{ width: 120 }} />
            </RouterLink>
          </Box>
        </Hidden>
        <Box p={2} display="none">
          <Box display="flex" justifyContent="center">
            <Avatar alt="User" className={classes.avatar} src={userAvatar} />
          </Box>
          <Box mt={2} textAlign="center">
            {/* <Link
              component={RouterLink}
              to="/app/account"
              variant="h5"
              color="textPrimary"
              underline="none"
            >
              {name}
            </Link> */}
            <Typography variant="h5" color="textPrimary">
              {user.nombre}
            </Typography>
          </Box>
        </Box>
        <Divider />
        <Box p={2}>
          {sections(user).map(section => (
            <List
              key={section.subheader}
              subheader={
                <ListSubheader disableGutters disableSticky>
                  {section.subheader}
                </ListSubheader>
              }
            >
              {renderNavItems({
                items: section.items,
                pathname: location.pathname
              })}
            </List>
          ))}
        </Box>
      </PerfectScrollbar>
    </Box>
  );

  return (
    <>
      <Hidden lgUp>
        <Drawer
          anchor="left"
          classes={{ paper: classes.mobileDrawer }}
          onClose={onMobileClose}
          open={openMobile}
          variant="temporary"
        >
          {content}
        </Drawer>
      </Hidden>
      <Hidden mdDown>
        <Drawer
          anchor="left"
          classes={{ paper: classes.desktopDrawer }}
          open
          variant="persistent"
        >
          {content}
        </Drawer>
      </Hidden>
    </>
  );
};

NavBar.propTypes = {
  onMobileClose: PropTypes.func,
  openMobile: PropTypes.bool
};

export default NavBar;
